/**
 * Runtime Reporter — Core
 * @module @plerity/runtime-reporter/reporter
 *
 * Captures `window.error` and `window.unhandledrejection` events, classifies
 * them into the same 5 categories as the test-time helper (`@plerity/testing-shared/playwright/utils/console-gate`),
 * fingerprint-dedupes client-side, queues in memory, and POSTs to
 * `/api/observability/errors` (handled by Phase 7's FastAPI router).
 *
 * The queue, chunking, retry, scrubbing, fingerprint and transports live in
 * `@plerity/observability`; this module keeps the public API
 * (`initRuntimeReporter`, `captureError`, `captureCspViolation`, `flush`), the
 * endpoint and the request body shape unchanged.
 *
 * Design constraints (from the plan):
 *   - No React, no Node APIs
 *   - Default-deny + allowlist (via shipped patterns or the YAML loader for tests)
 *   - PII-safe: scrubs query strings, templates token/id path segments, redacts
 *     credentials / email / phone / SSN in message, stack and metadata, never
 *     sends form data, anonymous session hash
 *   - Survives navigations: `keepalive` (bodies < 60 KiB) + `sendBeacon` on pagehide
 *   - Never silently loses a batch: network/5xx failures are requeued
 *
 * Wire-up pattern (each shell + Astro app):
 *   import { initRuntimeReporter } from '@plerity/runtime-reporter';
 *   initRuntimeReporter({ app: 'finance' });
 */

import {
  computeFingerprint,
  EventQueue,
  getSessionId,
  resetSessionId,
  type ScrubRouteOptions,
  scrubRoute,
  scrubText,
  scrubUrl,
  scrubValue,
  setErrorSink,
} from "@plerity/observability";
import { createBeaconSend, createFetchSend, reportWebVitals } from "@plerity/observability/web";
import type {
  CaptureErrorOptions,
  CspViolationDetails,
  ErrorCategory,
  ErrorSeverity,
  ReporterConfig,
  RuntimeErrorReport,
} from "./types";

// ---------------------------------------------------------------------------
// Classifier patterns — kept in sync with @plerity/testing-shared/playwright/utils/console-gate.ts
// ---------------------------------------------------------------------------

const CSP_PATTERNS: readonly RegExp[] = [
  /Refused to (apply|execute|load|frame|connect to|fetch|create)/i,
  /Content Security Policy directive/i,
  /violates the following Content Security Policy/i,
];

const MIME_PATTERNS: readonly RegExp[] = [
  /Refused to apply style/i,
  /Refused to execute script.*MIME/i,
  /was not loaded because its MIME type/i,
];

const MIXED_CONTENT_PATTERNS: readonly RegExp[] = [/Mixed Content/i];

const HYDRATION_PATTERNS: readonly RegExp[] = [
  /Hydration failed/i,
  /Text content does not match server-rendered HTML/i,
  /Hydration completed but contains mismatches/i,
  /Expected server HTML to contain/i,
];

const POLICY_PATTERNS: readonly RegExp[] = [...CSP_PATTERNS, ...MIME_PATTERNS, ...MIXED_CONTENT_PATTERNS];

function classify(message: string, hasStack: boolean): ErrorCategory {
  if (POLICY_PATTERNS.some((p) => p.test(message))) return "policy";
  if (HYDRATION_PATTERNS.some((p) => p.test(message))) return "hydration";
  // Distinguish 'console' (warning-grade) from 'page' (uncaught with stack).
  return hasStack ? "page" : "console";
}

// ---------------------------------------------------------------------------
// Queue (module-singleton)
// ---------------------------------------------------------------------------

/** A queued report plus its client-side fingerprint (the fingerprint is never sent). */
interface QueuedReport {
  fingerprint: string;
  report: RuntimeErrorReport;
}

/**
 * `maxPerFingerprint: 1` preserves the long-standing "one beacon per distinct
 * error per session" behavior; further identical occurrences are folded into
 * `metadata.repeat` on the pending report instead of being silently dropped.
 */
const queue = new EventQueue<QueuedReport>({
  maxPerFingerprint: 1,
  fingerprintOf: (item) => item.fingerprint,
  withRepeat: (item, repeat) => ({
    fingerprint: item.fingerprint,
    report: { ...item.report, metadata: { ...item.report.metadata, repeat } },
  }),
  serialize: (item) => JSON.stringify(item.report),
});

// ---------------------------------------------------------------------------
// Reporter state (module-singleton — initRuntimeReporter is idempotent)
// ---------------------------------------------------------------------------

interface ReporterState {
  config: Required<
    Omit<ReporterConfig, "buildSha" | "allowlistPatterns" | "onFlushed" | "routeTemplates" | "webVitals">
  > & {
    buildSha: string;
    onFlushed?: ReporterConfig["onFlushed"];
    routeTemplates: readonly string[];
  };
  allowlist: RegExp[];
  flushTimer: ReturnType<typeof setInterval> | null;
  initialized: boolean;
  // Registered window listeners, tracked so `_resetForTests` can remove them.
  // In production `initRuntimeReporter` runs once (idempotent) so this holds a
  // single generation; only the test-reset path relies on removal.
  listeners: Array<{ type: string; handler: EventListener }>;
}

const state: ReporterState = {
  config: {
    app: "",
    endpoint: "/api/observability/errors",
    buildSha: "unknown",
    sampleRate: 1.0,
    bufferSize: 10,
    flushIntervalMs: 5000,
    disabled: false,
    onFlushed: undefined,
    routeTemplates: [],
  },
  allowlist: [],
  flushTimer: null,
  initialized: false,
  listeners: [],
};

function scrubOptions(): ScrubRouteOptions {
  return { templates: state.config.routeTemplates };
}

function fetchSend() {
  return createFetchSend({ endpoint: state.config.endpoint, credentials: "same-origin" });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initRuntimeReporter(config: ReporterConfig): void {
  if (state.initialized) {
    // idempotent — silently no-op on re-init (HMR + StrictMode-safe)
    return;
  }
  // Expose captureError to low-tier packages (ui-core ErrorBoundary) that
  // cannot import this package. It no-ops while no `app` is configured.
  setErrorSink(captureError);
  if (config.disabled) {
    state.initialized = true;
    return;
  }
  state.config.app = config.app;
  state.config.endpoint = config.endpoint ?? state.config.endpoint;
  state.config.buildSha = config.buildSha ?? state.config.buildSha;
  state.config.sampleRate = config.sampleRate ?? state.config.sampleRate;
  state.config.bufferSize = config.bufferSize ?? state.config.bufferSize;
  state.config.flushIntervalMs = config.flushIntervalMs ?? state.config.flushIntervalMs;
  state.config.onFlushed = config.onFlushed;
  state.config.routeTemplates = config.routeTemplates ?? [];
  state.allowlist = (config.allowlistPatterns ?? []).map((p) => new RegExp(p));

  // Browser path — guard for SSR contexts where `window` is undefined.
  if (typeof window === "undefined") {
    state.initialized = true;
    return;
  }

  // Register via a helper that records the handler so `_resetForTests` can
  // remove it — without this, the module-singleton reset leaves stale
  // listeners bound to `window` and they accumulate across tests (vitest v4's
  // altered test sequencing turned that latent leak into a flaky failure in
  // the "fires on synthesized events" case).
  const register = (type: string, handler: EventListener): void => {
    window.addEventListener(type, handler);
    state.listeners.push({ type, handler });
  };

  register("error", ((event: ErrorEvent) => {
    void captureError(event.message ?? "Unknown error", event.error?.stack, "error");
  }) as EventListener);

  register("unhandledrejection", ((event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const msg = typeof reason === "string" ? reason : (reason?.message ?? "Unhandled promise rejection");
    const stack = typeof reason === "object" ? reason?.stack : undefined;
    void captureError(`Unhandled rejection: ${msg}`, stack, "error");
  }) as EventListener);

  // CSP violation capture — third listener added 2026-04-27 (defense-in-depth
  // for ARCH-4). Without this, a CSP that's silently broken in production
  // (e.g. a runtime URL not registered in package-runtime-urls.yaml) is
  // invisible until a user notices the symptom. Origin: 27-day social CSP
  // gap that was 100% silent. See agents/analysis/social_reports_csp_review_2026-04-27.md.
  register("securitypolicyviolation", ((event: SecurityPolicyViolationEvent) => {
    void captureCspViolation(event);
  }) as EventListener);

  // Periodic flush
  if (state.config.flushIntervalMs > 0) {
    state.flushTimer = setInterval(() => {
      void flush();
    }, state.config.flushIntervalMs);
  }

  // Best-effort flush on page hide / unload: sendBeacon when available (it is
  // queued by the browser and survives teardown), fetch keepalive otherwise.
  register("pagehide", (() => {
    void flushOnPageHide();
  }) as EventListener);

  if (config.webVitals) startWebVitals(config);

  state.initialized = true;
}

/** Default target of the Web Vitals ingest (every `*-api` server that mounts the runtime-errors sink serves it). */
export const DEFAULT_VITALS_ENDPOINT = "/api/observability/vitals";

/** Fire-and-forget: a vitals failure (missing chunk, unsampled session) must never affect the host app or the error path. */
function startWebVitals(config: ReporterConfig): void {
  const options = config.webVitals === true || !config.webVitals ? {} : config.webVitals;
  void reportWebVitals({
    endpoint: options.endpoint ?? DEFAULT_VITALS_ENDPOINT,
    app: config.app,
    buildSha: state.config.buildSha,
    sampleRate: options.sampleRate,
    routeTemplates: state.config.routeTemplates,
  }).catch(() => null);
}

/** Common report assembly: scrub everything, then truncate to the backend caps. */
function buildReport(
  category: ErrorCategory,
  severity: ErrorSeverity,
  message: string,
  stack: string | undefined,
  extraMetadata: Record<string, unknown> | undefined
): RuntimeErrorReport {
  const options = scrubOptions();
  const href = typeof window !== "undefined" ? window.location.href : undefined;
  const route = href ? scrubRoute(href, options) : undefined;
  const scrubbedMessage = scrubText(message).slice(0, 8000);
  const metadata = scrubValue(
    {
      build: state.config.buildSha,
      viewport: typeof window !== "undefined" ? { w: window.innerWidth, h: window.innerHeight } : undefined,
      ...(route ? { route } : {}),
      ...extraMetadata,
    },
    options
  ) as Record<string, unknown>;
  return {
    app: state.config.app,
    category,
    severity,
    // The backend requires a non-empty message; an empty one would 422 the whole batch.
    message: scrubbedMessage.length > 0 ? scrubbedMessage : "(no message)",
    stack: stack ? scrubText(stack).slice(0, 16000) : undefined,
    url: href ? scrubUrl(href, options) : undefined,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : undefined,
    session_id: getSessionId(),
    metadata,
  };
}

/**
 * Captures still hashing their fingerprint. `computeFingerprint` awaits
 * `crypto.subtle.digest`, which runs off the JS thread, so a fire-and-forget
 * listener (`void captureCspViolation(...)`) has no fixed macrotask distance
 * from "queued". Tracked here so tests can wait on the real condition.
 */
const inflight = new Set<Promise<void>>();

function track(work: Promise<void>): Promise<void> {
  inflight.add(work);
  const release = (): void => {
    inflight.delete(work);
  };
  work.then(release, release);
  return work;
}

function enqueue(fingerprint: string, report: RuntimeErrorReport): void {
  queue.push({ fingerprint, report });
  if (queue.size >= state.config.bufferSize) {
    void flush();
  }
}

/**
 * Programmatic CSP-violation capture.
 *
 * Builds a `RuntimeErrorReport` with `category: 'csp'` from a
 * SecurityPolicyViolationEvent. The CSP-specific event fields land in
 * `metadata.csp` (typed as `CspViolationDetails`) so the backend can
 * surface them in a dedicated dashboard view without breaking the
 * existing observability schema.
 *
 * The message is synthesised in a stable, fingerprint-friendly form:
 *   `CSP: <effectiveDirective> blocked <blockedURI>`
 * which lets the existing `computeFingerprint` collapse repeat violations
 * of the same blocked-URI / directive pair into a single occurrence.
 */
export function captureCspViolation(event: SecurityPolicyViolationEvent): Promise<void> {
  return track(runCaptureCspViolation(event));
}

async function runCaptureCspViolation(event: SecurityPolicyViolationEvent): Promise<void> {
  if (!state.initialized || state.config.disabled || !state.config.app) return;

  // Allow callers to drop noisy violations via the same allowlist used for errors
  // (e.g. ad-blocker injections, browser-extension content scripts).
  // The synthesised message is what the allowlist matches against.
  const blockedURI = event.blockedURI || "(unknown)";
  const effective = event.effectiveDirective || event.violatedDirective || "(unknown)";
  const rawMessage = `CSP: ${effective} blocked ${blockedURI}`;

  if (state.allowlist.some((rx) => rx.test(rawMessage))) return;

  // The reported message carries the blocked URI without its query / hash and
  // with token-looking path segments templated (a blocked URL can embed one).
  const message = `CSP: ${effective} blocked ${scrubValue(blockedURI, scrubOptions(), "blockedURI") as string}`;

  // Use blockedURI + effectiveDirective + sourceFile/line as the fingerprint
  // input. The classify() function would tag this as 'policy' from the
  // message regex, but we want 'csp' specifically so dashboards can
  // distinguish event-fired CSP violations from regex-classified policy
  // errors (which can come from console messages of unrelated origin).
  const fingerprintStack = [
    event.sourceFile ?? "",
    event.lineNumber ? `${event.lineNumber}` : "",
    event.columnNumber ? `${event.columnNumber}` : "",
  ]
    .filter(Boolean)
    .join(":");
  const fingerprint = await computeFingerprint(message, fingerprintStack, "csp");

  const csp: CspViolationDetails = {
    blockedURI: event.blockedURI || undefined,
    violatedDirective: event.violatedDirective || undefined,
    effectiveDirective: event.effectiveDirective || undefined,
    disposition: event.disposition || undefined,
    sourceFile: event.sourceFile || undefined,
    lineNumber: event.lineNumber || undefined,
    columnNumber: event.columnNumber || undefined,
    sample: event.sample ? event.sample.slice(0, 40) : undefined,
    originalPolicy: event.originalPolicy ? event.originalPolicy.slice(0, 1000) : undefined,
  };

  enqueue(fingerprint, buildReport("csp", "error", message, undefined, { csp }));
}

/**
 * Programmatic capture — useful for caught-but-significant errors.
 *
 * @param options - Optional {@link CaptureErrorOptions}: force a `category`
 *   (skips message-regex classification) and/or merge structured `metadata`
 *   into the report. Used by the shared `createFetcher` to report caught
 *   non-2xx API responses as `category: 'network'` with endpoint/status/etc.
 */
export function captureError(
  message: string,
  stack: string | undefined,
  severity: ErrorSeverity = "error",
  options?: CaptureErrorOptions
): Promise<void> {
  return track(runCaptureError(message, stack, severity, options));
}

async function runCaptureError(
  message: string,
  stack: string | undefined,
  severity: ErrorSeverity,
  options: CaptureErrorOptions | undefined
): Promise<void> {
  if (!state.initialized || state.config.disabled || !state.config.app) return;

  // Sample non-error severities only
  if (severity !== "error" && Math.random() > state.config.sampleRate) return;

  // Allowlist drop (matches the raw message, before scrubbing)
  if (state.allowlist.some((rx) => rx.test(message))) return;

  const category = options?.category ?? classify(message, !!stack);
  // Fingerprint the SCRUBBED text: the hash never leaves the client, and two
  // errors that differ only in an email / phone / token are the same error.
  const fingerprint = await computeFingerprint(scrubText(message), stack ? scrubText(stack) : undefined, category);

  enqueue(fingerprint, buildReport(category, severity, message, stack, options?.metadata));
}

/**
 * Force-flush the queue. Returns the number of reports the server accepted.
 * Requests are chunked to <= 60 KiB; a network failure / 5xx requeues the
 * unsent reports for the next flush instead of discarding them.
 */
export async function flush(): Promise<number> {
  if (!state.initialized || queue.size === 0) return 0;
  const sent = await queue.flush(fetchSend());
  if (sent > 0) state.config.onFlushed?.(sent);
  return sent;
}

async function flushOnPageHide(): Promise<number> {
  if (!state.initialized || queue.size === 0) return 0;
  const send = createBeaconSend({ endpoint: state.config.endpoint, fallback: fetchSend() });
  const sent = await queue.flush(send);
  if (sent > 0) state.config.onFlushed?.(sent);
  return sent;
}

/** Test-only: resolve once every in-flight capture (fingerprint hashing) has been queued or dropped. */
export async function _settleForTests(): Promise<void> {
  while (inflight.size > 0) {
    await Promise.allSettled([...inflight]);
  }
}

/** Test-only: reset module state (NOT for production use). */
export function _resetForTests(): void {
  if (state.flushTimer) clearInterval(state.flushTimer);
  state.flushTimer = null;
  // Remove any window listeners registered by a prior initRuntimeReporter so
  // they do not accumulate across tests (see ReporterState.listeners).
  if (typeof window !== "undefined") {
    for (const { type, handler } of state.listeners) {
      window.removeEventListener(type, handler);
    }
  }
  state.listeners = [];
  queue.clear();
  state.allowlist = [];
  state.initialized = false;
  state.config.app = "";
  state.config.disabled = false;
  state.config.onFlushed = undefined;
  state.config.routeTemplates = [];
  setErrorSink(null);
  resetSessionId();
}
